import React from 'react';
import { Text } from '@zillow/constellation-10';
import { format, isValid } from 'date-fns';
import {
  HeadWrapper,
  LoadingConstants,
  RentalManagerUserManagementCompanyWebApiHandler_getActivityHistory,
} from '@zg-rentals/rental-manager-env';
import type { UserManagementActivityDto } from '@zg-rentals/rental-manager-env';
import type { ColumnConfig } from '@zg-rentals/advanced-table';
import { AdvancedTable } from '@zg-rentals/advanced-table';

const columnConfig: Array<ColumnConfig<UserManagementActivityDto>> = [
  {
    width: 2,
    align: 'left',
    header: 'Activity Date',
    renderer: (data) => {
      const date = new Date(parseFloat(data.created));
      if (!isValid(date)) {
        return <div>-</div>;
      }
      return (
        <div>
          <div>{format(date, 'M/dd/yyyy')}</div>
          <div>{format(date, 'hh:mm a')}</div>
        </div>
      );
    },
  },
  {
    width: 3,
    align: 'center',
    header: 'Changed by',
    renderer: (data) => data.changedByUserId || '-',
  },
  {
    width: 4,
    header: 'Change',
    align: 'center',
    renderer: (data) => {
      switch (data.activityType) {
        case 'createdCompany':
          return 'Created Company';
        case 'changedCompanyName':
          return `Changed Company Name: ${data.newValue}`;
        case 'addedInitialUser':
          return 'Added User';
        case 'impersonatedUser':
          return 'Impersonated User';
        case 'sentInvitation':
          return 'Sent Invitation';
        case 'removedUser':
          return 'Removed User from Company';
        case 'cancelledInvitation':
          return 'Cancelled Invitation';
        case 'updatedInvitation':
          return 'Updated Invitation';
        case 'acceptedInvitation':
          return 'Accepted Invitation';
        case 'declinedInvitation':
          return 'Declined Invitation';
        case 'addedPropertyToCompany':
          return 'Added Property';
        case 'removedPropertyFromCompany':
          return 'Removed Property';
        case 'addedPermission':
          return 'Added Permission';
        case 'removedPermission':
          return 'Removed Permission';
        case 'addedPropertyAccess':
          return 'Added Property Access';
        case 'removedPropertyAccess':
          return 'Removed Property Access';
        case 'grantedFullPortfolioAccess':
          return 'Granted Full Portfolio Access';
        case 'removedFullPortfolioAccess':
          return 'Removed Full Portfolio Access';
        case 'createApplicationManager':
          return 'Added Application Manager Permission';
        case 'applicationManagerTransfer':
          return 'Transferred Application Manager Permission';
        case 'applicationPermissionRemoved':
          return 'Removed Application Permission';
        case 'applicationPermissionGranted':
          return 'Granted Application Permission';
        default:
          return data.newValue;
      }
    },
  },
  {
    width: 3,
    header: 'User',
    align: 'center',
    renderer: (data) => data.changedUserEmail || '-',
  },
];

const ActivityHistoryPage = () => {
  const [logs, setLogs] = React.useState<Array<UserManagementActivityDto>>([]);
  const [loadingState, setLoadingState] = React.useState<string>(LoadingConstants.INITIAL);

  const [error, setError] = React.useState<string | null>(null);
  const [hasMoreData, setHasMoreData] = React.useState(false);

  React.useEffect(() => {
    getActivityHistory();
  }, []);

  const getActivityHistory = () => {
    let startTimestamp;
    if (logs.length > 0) {
      startTimestamp = logs[logs.length - 1].reverseCreatedMs;
    }
    setLoadingState(LoadingConstants.LOADING);
    RentalManagerUserManagementCompanyWebApiHandler_getActivityHistory({ startTimestamp })
      .then((activityList) => {
        if (loadingState == LoadingConstants.LOADING) {
          return;
        }
        setLogs(logs.concat(activityList.userManagementActivityList));
        setHasMoreData(activityList.userManagementActivityList.length >= activityList.pageSize);
        setLoadingState(LoadingConstants.SUCCESS);
        return;
      })
      .catch(() => {
        setLoadingState(LoadingConstants.ERROR);
        setError('Failed to get activity history');
      });
  };

  const renderEmptyBanner = () => {
    if (logs.length === 0 && loadingState === LoadingConstants.SUCCESS) {
      return <Text className="empty-message">No activity found</Text>;
    }
    return null;
  };

  const handleInfiniteScroll = (isVisible: boolean) => {
    if (isVisible && hasMoreData) {
      getActivityHistory();
    }
  };

  return (
    <div className="ActivityHistoryPage">
      <HeadWrapper title="Users" />
      <AdvancedTable
        columnConfig={columnConfig}
        data={logs}
        hasMoreData={hasMoreData}
        isLoading={loadingState === LoadingConstants.LOADING || loadingState === LoadingConstants.INITIAL}
        handleLoadMoreData={handleInfiniteScroll}
      />
      {renderEmptyBanner()}
    </div>
  );
};

export default ActivityHistoryPage;
